
import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import api from '../utils/api';
import { deleteReport as deleteReportApi } from '../utils/deleteReport';
import styles from './ReportDetailsPage.module.css';
import LogoSpinner from '../components/Spinner/LogoSpinner';
import { resolveEvidenceImageUrl, CLOUDINARY_TRANSFORMS } from '../utils/getImageUrl';

const toTitleCase = (str) => str.replace(/\b\w/g, c => c.toUpperCase());
const toSentenceCase = (str) => str ? str.charAt(0).toUpperCase() + str.slice(1) : str;

const CATEGORIES = [
  'Health Hazard', 'Security Hazard', 'Fire Hazard', 'Structural Hazard', 'Environmental Hazard',
  'Well Maintained Property', 'Responsive Landlord', 'Fair Rent', 'Safe and Secure', 'Good Condition',
];

const ReportDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const user = useSelector(state => state.user.user);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveError, setSaveError] = useState(null);
  // Only allow delete if user is the report owner
  // Fallback: if report.user_id is missing, allow delete if user.id === report.id (for debugging)
  const canDelete = user && report && (report.user_id ? user.id === report.user_id : user.id === report.id);

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this report? This cannot be undone.')) return;
    setDeleteLoading(true);
    setDeleteError(null);
    try {
      await deleteReportApi(id);
      navigate('/'); // Redirect to home or reports list
    } catch (err) {
      setDeleteError(err?.response?.data?.message || 'Failed to delete report');
    } finally {
      setDeleteLoading(false);
    }
  };




  const handleEditStart = useCallback(() => {
    setEditData({
      title: report.title || '',
      description: report.description || '',
      city: report.city || '',
      postcode: report.postcode || '',
      street: report.street || '',
      flat_number: report.flat_number || '',
      property_type: report.property_type || '',
      landlord_or_agency: report.landlord_or_agency || '',
      advert_source: report.advert_source || '',
      category: report.category || '',
      is_anonymous: report.is_anonymous,
    });
    setEditing(true);
    setSaveError(null);
  }, [report]);

  const handleSave = async () => {
    setSaveLoading(true);
    setSaveError(null);
    try {
      const res = await api.patch(`/reports/${id}`, editData);
      setReport(prev => ({ ...prev, ...res.data }));
      setEditing(false);
    } catch (err) {
      setSaveError(err?.response?.data?.message || 'Failed to save changes');
    } finally {
      setSaveLoading(false);
    }
  };

  const setField = (field, transform = v => v) => (e) =>
    setEditData(prev => ({ ...prev, [field]: transform(e.target.value) }));

  const [currentImage, setCurrentImage] = useState(0);
  const [comments, setComments] = useState([]);
  const [commentTotal, setCommentTotal] = useState(0);
  const [commentPage, setCommentPage] = useState(1);
  const [commentTotalPages, setCommentTotalPages] = useState(1);
  const [newComment, setNewComment] = useState('');
  const [replyTo, setReplyTo] = useState(null);
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [replyContent, setReplyContent] = useState('');
  const [replyIsAnonymous, setReplyIsAnonymous] = useState(false);

  useEffect(() => {
    const fetchReport = async () => {
      try {
        const eventId = crypto.randomUUID();
        const res = await api.get(`/reports/${id}`, { headers: { 'x-event-id': eventId } });
        setReport(res.data);
        if (window.fbq) window.fbq('track', 'ViewContent', {}, { eventID: eventId });
      } catch (err) {
        setError('Failed to load report details');
      } finally {
        setLoading(false);
      }
    };
    fetchReport();
  }, [id]);

  const fetchComments = async (page = 1) => {
    try {
      const res = await api.get(`/reports/${id}/comments`, { params: { page, limit: 20 } });
      setComments(res.data.data || []);
      setCommentTotal(res.data.total || 0);
      setCommentPage(res.data.page || 1);
      setCommentTotalPages(res.data.totalPages || 1);
    } catch (err) {
      //  handle error
    }
  };
  useEffect(() => {
    if (id) fetchComments(1);
    // eslint-disable-next-line
  }, [id]);



  if (loading) return <LogoSpinner message="Loading report..." />;
  if (error) return <div className="alert alert-danger">{error}</div>;
  if (!report) return <div className="alert alert-secondary">No report found.</div>;

  // Images: report.evidence_files (array of filenames)
  const images = Array.isArray(report.evidence_files) ? report.evidence_files : [];
  const getImageSrc = (image) => {
    if (!image) return null;
    return resolveEvidenceImageUrl({
      evidenceUrl: image.file_url,
      evidenceFileName: image.file_name,
      transform: CLOUDINARY_TRANSFORMS.display
    });
  };

  const handlePrev = () => {
    setCurrentImage((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };
  const handleNext = () => {
    setCurrentImage((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  // Recursive comment rendering
  const renderComments = (commentsArr = [], parentId = null, level = 0) => (
    <div style={{ marginLeft: level * 24 }}>
      {commentsArr.map(comment => (
        <div key={comment.id} className={styles.commentBox}>
          <div className={styles.commentHeader}>
            <span className={styles.commentAuthor}>{comment.is_anonymous ? 'Anonymous' : 'User'}</span>
            <span className={styles.commentDate}>{new Date(comment.created_at).toLocaleString()}</span>
          </div>
          <div className={styles.commentText}>{comment.content}</div>
          <button className={styles.replyBtn} onClick={() => {
            setReplyTo(comment.id);
            setReplyContent('');
            setReplyIsAnonymous(false);
          }}>Reply</button>
          {replyTo === comment.id && (
            <form
              className={styles.replyForm}
              onSubmit={async e => {
                e.preventDefault();
                if (!replyContent.trim()) return;
                try {
                  await api.post(`/reports/${id}/comments`, {
                    content: replyContent,
                    parent_comment_id: comment.id,
                    is_anonymous: replyIsAnonymous,
                  });
                  setReplyContent('');
                  setReplyIsAnonymous(false);
                  setReplyTo(null);
                  fetchComments(commentPage);
                } catch (err) {
                  //  handle error
                }
              }}
            >
              <textarea
                className={styles.replyInput}
                placeholder="Write a reply..."
                rows={2}
                autoFocus
                value={replyContent}
                onChange={e => setReplyContent(e.target.value)}
              />
              <label className={styles.anonCheckboxLabel}>
                <input
                  type="checkbox"
                  checked={replyIsAnonymous}
                  onChange={e => setReplyIsAnonymous(e.target.checked)}
                />
                Post as Anonymous
              </label>
              <button type="submit" className={styles.replySubmitBtn}>Post Reply</button>
              <button type="button" className={styles.replyCancelBtn} onClick={() => setReplyTo(null)}>Cancel</button>
            </form>
          )}
          {comment.replies && comment.replies.length > 0 && renderComments(comment.replies, comment.id, level + 1)}
        </div>
      ))}
    </div>
  );

  return (
    <div className="container my-4">
      <div className="card shadow p-4 mb-4">
        <div className="position-relative mb-3">
          {report.is_flagged && (
            <span className="position-absolute top-0 end-0 m-2" title="Flagged as negative/sensitive">
              <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
                <polygon points="20,5 37,35 3,35" fill="#e74c3c" />
                <rect x="19" y="15" width="2" height="10" rx="1" fill="#fff" />
                <rect x="19" y="27" width="2" height="2" rx="1" fill="#fff" />
              </svg>
            </span>
          )}
          {editing ? (
            <input
              className="form-control form-control-lg mb-2"
              value={editData.title}
              onChange={setField('title', toSentenceCase)}
              placeholder="Title"
            />
          ) : (
            <h2 className="mb-2">{report.title}</h2>
          )}
          <div className="d-flex flex-wrap gap-3 small text-muted mb-2">
            <span><strong>Location:</strong> {editing ? editData.city : (report.city || report.location)}</span>
            <span><strong>Date:</strong> {report.created_at ? new Date(report.created_at).toLocaleString() : 'N/A'}</span>
          </div>
        </div>
        {canDelete && (
          <div className="d-flex justify-content-end gap-2 mb-2">
            {!editing && (
              <button className="btn btn-outline-primary btn-sm" onClick={handleEditStart}>
                Edit
              </button>
            )}
            {!editing && (
              <button className="btn btn-danger btn-sm" onClick={handleDelete} disabled={deleteLoading}>
                {deleteLoading ? 'Deleting...' : 'Delete'}
              </button>
            )}
          </div>
        )}
        {deleteError && <div className="alert alert-danger py-2 mb-2">{deleteError}</div>}
        {images.length > 0 && getImageSrc(images[currentImage]) && (
          <div className="d-flex align-items-center justify-content-center mb-3 gap-2">
            <button className="btn btn-outline-secondary btn-sm" onClick={handlePrev}>&lt;</button>
            <img
              className="rounded border"
              style={{ maxHeight: 260, maxWidth: '100%' }}
              src={getImageSrc(images[currentImage])}
              alt={`Evidence ${currentImage + 1}`}
            />
            <button className="btn btn-outline-secondary btn-sm" onClick={handleNext}>&gt;</button>
            <div className="ms-2 small text-muted">{currentImage + 1} / {images.length}</div>
          </div>
        )}
        {editing ? (
          <>
            <div className="mb-3">
              <label className="form-label small text-muted">Description</label>
              <textarea
                className="form-control mb-2"
                rows={4}
                value={editData.description}
                onChange={setField('description', toSentenceCase)}
              />
            </div>
            <div className="row g-2 mb-3">
              <div className="col-6 col-md-4">
                <label className="form-label small text-muted">City</label>
                <input className="form-control form-control-sm" value={editData.city} onChange={setField('city', toTitleCase)} />
              </div>
              <div className="col-6 col-md-4">
                <label className="form-label small text-muted">Postcode</label>
                <input className="form-control form-control-sm" value={editData.postcode} onChange={setField('postcode', v => v.toUpperCase())} />
              </div>
              <div className="col-6 col-md-4">
                <label className="form-label small text-muted">Street</label>
                <input className="form-control form-control-sm" value={editData.street} onChange={setField('street', toTitleCase)} />
              </div>
              <div className="col-6 col-md-4">
                <label className="form-label small text-muted">Flat/Unit Number</label>
                <input className="form-control form-control-sm" value={editData.flat_number} onChange={setField('flat_number', toTitleCase)} />
              </div>
              <div className="col-6 col-md-4">
                <label className="form-label small text-muted">Property Type</label>
                <input className="form-control form-control-sm" value={editData.property_type} onChange={setField('property_type', toTitleCase)} />
              </div>
              <div className="col-6 col-md-4">
                <label className="form-label small text-muted">Landlord/Agency</label>
                <input className="form-control form-control-sm" value={editData.landlord_or_agency} onChange={setField('landlord_or_agency', toTitleCase)} />
              </div>
              <div className="col-6 col-md-4">
                <label className="form-label small text-muted">Advert Source</label>
                <input className="form-control form-control-sm" value={editData.advert_source} onChange={setField('advert_source', toTitleCase)} />
              </div>
              <div className="col-6 col-md-4">
                <label className="form-label small text-muted">Category</label>
                <select
                  className="form-select form-select-sm"
                  value={editData.category}
                  onChange={setField('category')}
                >
                  <option value="">Select category</option>
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="col-6 col-md-4 d-flex align-items-end">
                <div className="form-check mb-1">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="editAnon"
                    checked={editData.is_anonymous}
                    onChange={e => setEditData(prev => ({ ...prev, is_anonymous: e.target.checked }))}
                  />
                  <label className="form-check-label small" htmlFor="editAnon">Anonymous</label>
                </div>
              </div>
            </div>
            {saveError && <div className="alert alert-danger py-2 mb-2">{saveError}</div>}
            <div className="d-flex gap-2 mb-2">
              <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saveLoading}>
                {saveLoading ? 'Saving...' : 'Save Changes'}
              </button>
              <button className="btn btn-outline-secondary btn-sm" onClick={() => setEditing(false)} disabled={saveLoading}>
                Cancel
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="mb-3">{report.description}</div>
            <div className="row g-2 mb-3">
              <div className="col-6 col-md-4"><strong>Postcode:</strong> {report.postcode || 'N/A'}</div>
              <div className="col-6 col-md-4"><strong>Street:</strong> {report.street || 'N/A'}</div>
              <div className="col-6 col-md-4"><strong>Property Type:</strong> {report.property_type || 'N/A'}</div>
              <div className="col-6 col-md-4"><strong>Landlord/Agency:</strong> {report.landlord_or_agency || 'N/A'}</div>
              <div className="col-6 col-md-4"><strong>Advert Source:</strong> {report.advert_source || 'N/A'}</div>
              <div className="col-6 col-md-4"><strong>Category:</strong> {report.category || 'N/A'}</div>
              <div className="col-6 col-md-4"><strong>Anonymous:</strong> {report.is_anonymous ? 'Yes' : 'No'}</div>
            </div>
          </>
        )}
      </div>
      {/* Comments Section */}
      <div className="card shadow p-4">
        <h3 className="mb-3 d-flex align-items-center gap-2">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{height: '1.5em'}}><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
          Comments <span className="fw-normal text-primary">({commentTotal})</span>
        </h3>
        <form
          className="mb-3"
          onSubmit={async e => {
            e.preventDefault();
            if (!newComment.trim()) return;
            try {
              await api.post(`/reports/${id}/comments`, {
                content: newComment,
                is_anonymous: isAnonymous,
              });
              setNewComment('');
              setIsAnonymous(false);
              fetchComments(1);
            } catch (err) {
              //  handle error
            }
          }}
        >
          <textarea
            className="form-control mb-2"
            placeholder="Write a comment..."
            rows={3}
            value={newComment}
            onChange={e => setNewComment(e.target.value)}
          />
          <div className="form-check mb-2">
            <input
              className="form-check-input"
              type="checkbox"
              checked={isAnonymous}
              onChange={e => setIsAnonymous(e.target.checked)}
              id="anonCheck"
            />
            <label className="form-check-label" htmlFor="anonCheck">
              Post as Anonymous
            </label>
          </div>
          <button type="submit" className="btn btn-primary btn-sm">Post Comment</button>
        </form>
        <div>
          {renderComments(comments)}
        </div>
        {commentTotalPages > 1 && (
          <div className="d-flex justify-content-center align-items-center gap-3 mt-3">
            <button
              className="btn btn-outline-secondary btn-sm"
              disabled={commentPage <= 1}
              onClick={() => fetchComments(commentPage - 1)}
            >
              Previous
            </button>
            <span className="small text-muted">Page {commentPage} of {commentTotalPages}</span>
            <button
              className="btn btn-outline-secondary btn-sm"
              disabled={commentPage >= commentTotalPages}
              onClick={() => fetchComments(commentPage + 1)}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportDetailsPage;
